import { useEffect, useMemo, useState } from 'react'
import { useOnboardingStore, STEPS } from '../../stores/onboardingStore'
import { stepHasErrors } from '../../lib/stepSummary'

// Phase groups are derived from each step's `group` field, in array order, so
// reordering STEPS automatically regroups the rail. Each step carries its
// 1-based position (`num`) for navigation + progress math.
function buildGroups() {
  const result = []
  let current = null
  STEPS.forEach((step, idx) => {
    if (step.group !== current) {
      result.push({ label: step.group, steps: [] })
      current = step.group
    }
    result[result.length - 1].steps.push({ ...step, num: idx + 1 })
  })
  return result
}

function sectionPct([start, end], currentStep) {
  if (currentStep < start) return 0
  if (currentStep > end) return 100
  return ((currentStep - start) / Math.max(1, end - start)) * 100
}

function initialsFromName(fullName) {
  const v = String(fullName ?? '').trim()
  if (!v) return ''
  const words = v.split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  if (words.length === 1) return words[0][0].toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2.5 6.5L5 9L9.5 3.5" />
    </svg>
  )
}

export default function StepRail() {
  const currentStep = useOnboardingStore((s) => s.currentStep)
  const goToStep = useOnboardingStore((s) => s.goToStep)
  const formData = useOnboardingStore((s) => s.formData)
  const liteMode = useOnboardingStore((s) => s.liteMode)

  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000)
    return () => clearInterval(id)
  }, [])

  const rawName = formData?.details?.practiceName
  const practiceName = rawName ? rawName.trim() : ''
  const firstOwnerName = String(
    formData?.owners?.owners?.[0]?.fullName ?? '',
  ).trim()
  const initials = initialsFromName(firstOwnerName)
  const isNetworkPlus = formData?.networkplus?.isMemberPlusMember === true
  const showChipsRow = isNetworkPlus
  // Kits ALWAYS ship — even under Network+ with no own providers, the demo kit
  // defaults to the practice owner. Never render kits as skipped/"not needed".

  const groups = useMemo(buildGroups, [])

  const totalSteps = liteMode ? 10 : STEPS.length
  const progressPct = (currentStep / totalSteps) * 100

  // Estimated minutes per step (rough, based on form complexity)
  const STEP_MINUTES = [2, 2, 1, 2, 1, 1, 2, 0.5, 0.5, 1]
  const minutesRemaining = STEP_MINUTES.slice(currentStep - 1).reduce(
    (a, b) => a + b,
    0,
  )
  const timeLabel =
    minutesRemaining <= 1
      ? 'Less than a minute left'
      : `~${Math.ceil(minutesRemaining)} min remaining`

  return (
    <aside
      className={`step-rail${liteMode ? ' is-lite-rail' : ''}`}
      aria-label="Onboarding steps"
    >
      <div className="rail-header">
        {practiceName && (
          <div className="rail-ws-practice">{practiceName}</div>
        )}

        {firstOwnerName && (
          <div className="rail-ws-owner">
            <span className="rail-ws-avatar" aria-hidden="true">
              {initials}
            </span>
            <span className="rail-ws-owner-name">{firstOwnerName}</span>
          </div>
        )}

        {showChipsRow && (
          <div className="rail-ws-chips">
            {isNetworkPlus && (
              <span className="rail-ws-chip">Network+ enabled</span>
            )}
          </div>
        )}

        <div className="rail-ws-progress">
          <div className="rail-ws-progress-meta">
            <span className="rail-ws-progress-count">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="rail-ws-time-est">
              {timeLabel}
            </span>
          </div>
          <div className="rail-ws-progress-bar">
            <div
              className="rail-ws-progress-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      <div className="rail-sections">
      {groups.map((group) => {
        const start = group.steps[0].num
        const end = group.steps[group.steps.length - 1].num
        const pct = sectionPct([start, end], currentStep)
        return (
          <div
            key={group.label}
            className="rail-section"
            style={{ '--fill-pct': pct + '%' }}
          >
            <div className="rail-section-label">{group.label}</div>
            <ol>
              {group.steps.map((step) => {
                const num = step.num
                let state =
                  num < currentStep
                    ? 'done'
                    : num === currentStep
                      ? 'current'
                      : 'upcoming'
                const hasErr =
                  state === 'done' &&
                  stepHasErrors(step.id, formData)
                if (hasErr) state = 'done-error'
                // Keep the rail a clean single-line nav: only surface a second
                // line when it matters (needs-attention). Data summaries are
                // dropped so the steps fit the viewport exactly.
                const summary = hasErr ? 'Needs attention' : null
                const interactive =
                  state === 'done' || state === 'done-error'
                const inner = (
                  <>
                    <span className="step-rail-marker">
                      {state === 'done' ? (
                        <CheckIcon />
                      ) : (
                        String(num).padStart(2, '0')
                      )}
                    </span>
                    <span className="step-rail-main">
                      <span className="step-rail-label">{step.label}</span>
                      <span className="step-rail-summary">{summary || null}</span>
                    </span>
                  </>
                )
                return (
                  <li
                    key={step.id}
                    data-state={state}
                  >
                    {interactive ? (
                      <button type="button" onClick={() => goToStep(num)}>
                        {inner}
                      </button>
                    ) : (
                      <div className="step-rail-static" aria-disabled="true">
                        {inner}
                      </div>
                    )}
                  </li>
                )
              })}
            </ol>
          </div>
        )
      })}
      </div>

    </aside>
  )
}
